import React, {
	memo,
	useCallback,
	useMemo,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import { Form } from 'semantic-ui-react';

import { log } from '../../services/utils';
import { wordCount } from '../../services/validation';

import DescriptionTooltip from './DescriptionTooltip';
import HelpModal from '../Shared/HelpModal';
import ValidationPopup from './ValidationPopup';

import styles from './form.module.css';

const FormTextArea = ( {
	align = 'left',
	errors = [],
	fieldLabel,
	forceValidation,
	helpId,
	instructions,
	maxWords,
	name,
	onChange,
	readOnly,
	required,
	spellcheck = true,
	value,
	useTooltip,
	width,
	...props
} ) => {
	const inputRef = useRef( null );

	const [ validate, setValidate ] = useState( false );
	const [ validationPopup, setValidationPopup ] = useState( false );

	const isInvalid = useCallback(
		() => validate || forceValidation
			? errors.some( error => error.error )
			: false,
		[
			errors,
			forceValidation,
			validate
		]
	);
	const hasFocus = useCallback( () => inputRef.current === document.activeElement, [] );

	const handleChange = useCallback( e => {
		// disable validation while user is entering a value
		if ( validate ) {
			setValidate( false );
		}

		// disable validationPopup while user is entering a value
		if ( validationPopup ) {
			setValidationPopup( false );
		}

		// update formData
		onChange( e );
	}, [
		onChange,
		validate,
		validationPopup
	] );

	const handleFocus = useCallback( () => {
		if (
			!validationPopup
			&& isInvalid()
			&& hasFocus()
		) {
			setValidationPopup( true );
		} else if ( validationPopup ) {
			setValidationPopup( false );
		}
	}, [
		hasFocus,
		isInvalid,
		validationPopup
	] );

	const handleBlur = useCallback( () => {
		// enable validation when field loses focus
		if ( errors.length && !validate ) {
			setValidate( true );
		}

		// disable validationPopup when field loses focus
		if ( validationPopup ) {
			setValidationPopup( false );
		}
	}, [
		errors.length,
		validate,
		validationPopup
	] );

	const alignment = useMemo(
		() => align === 'right'
			? { marginLeft: 'auto' }
			: null,
		[ align ]
	);

	const wordCountStyles = useMemo(
		() => maxWords && wordCount( value ) > maxWords
			? { color: '#9F3A38' }
			: null,
		[ maxWords, value ]
	);

	return (
		<>
			{ log( `- ${ name } input` ) }
			<Form.Field
				error={ isInvalid() }
				required={ !readOnly && required }
				style={ alignment }
				width={ width }
			>
				{
					fieldLabel
						? <label>
							{ helpId
								? <HelpModal
									id={ helpId }
									triggerPosition="left"
									triggerSize="small"
								/>
								: null
							}
							{ fieldLabel }
							{ instructions && useTooltip
								? <>&nbsp;&nbsp;<DescriptionTooltip text={ instructions } /></>
								: null
							}
						</label>
						: null
				}
				{ instructions & !useTooltip
					? <div
						dangerouslySetInnerHTML={ { __html: instructions } }
						className={ styles.instructions }
						style={ { marginBottom: '1rem' } }
					/>
					: null
				}
				{ readOnly
					? <p style={ { marginBottom: 0 } }>{ value || '--' }</p>
					: <textarea
						autoComplete="chrome-off"
						name={ name }
						onBlur={ handleBlur }
						onChange={ handleChange }
						onFocus={ handleFocus }
						ref={ inputRef }
						spellCheck={ spellcheck }
						value={ value }
						{ ...props }
					/>
				}
				<p
					className={ styles.wordCount }
					style={ wordCountStyles }
				>
					<small>
						{ maxWords
							? `${ wordCount( value ) } / ${ maxWords }`
							: null
						}
					</small>
				</p>
			</Form.Field>
			<ValidationPopup
				context={ inputRef }
				errors={ errors }
				open={ validationPopup }
			/>
		</>
	);
};

FormTextArea.propTypes = {
	align: PropTypes.string,
	errors: PropTypes.arrayOf( PropTypes.shape( {
		error: PropTypes.bool,
		message: PropTypes.node
	} ) ),
	fieldLabel: PropTypes.node,
	forceValidation: PropTypes.bool,
	helpId: PropTypes.number,
	instructions: PropTypes.string,
	maxWords: PropTypes.number,
	name: PropTypes.string,
	onChange: PropTypes.func,
	readOnly: PropTypes.bool,
	required: PropTypes.bool,
	spellcheck: PropTypes.bool,
	useTooltip: PropTypes.bool,
	value: PropTypes.string,
	width: PropTypes.number
};

export default memo( FormTextArea );
